/**
 * The register's ONE table shell (workflow UX sweep spec §8.3, D12): the
 * built-in register and a module's view (the Compass pricing view) both
 * render through it. It owns the `<colgroup>` (defaults overridden by
 * `ui.records.columnWidths[viewId]`), the header rows (one, or two for
 * grouped columns), the resize handles, the sticky header inside the
 * scrolling card, the row handlers, the marked row (D15), the clamp of a
 * free-text cell and the footer slot (the pager, outside the scroll area).
 * Pure layout rules live in `registerColumns.ts`; this file only renders.
 * SA-1124: a column with a `sortKey` heads with a sort button when the caller passes `onSort`; `aria-sort` marks the sorted column; a header's `title` is its tip, never a native `title`.
 * SA-1185: the header words are the shared `SortHeader.tsx`, and a click cycles from the column's own first click (`sortFirst`, else the register's), so a module's table sorts any key it names.
 */
import {
  useCallback, useLayoutEffect, useMemo, useRef, useState,
  type CSSProperties, type KeyboardEvent, type PointerEvent, type ReactElement, type ReactNode,
} from 'react'
import { cn } from '@/utils'
import { Tip } from '@/components/ui'
import { RESIZE_TIP } from '@/components/ui/panelWidth'
import { useSettingsStore } from '@/stores/settingsStore'
import type { Order } from '@/types/commerce'
import { ClampedText } from './ClampedText'
import { ariaSort, cycleSort } from './clientSort'
import { firstClick, type RegisterSort, type SortDir } from './registerSort'
import { SortHeaderLabel } from './SortHeader'
import { columnWidth, boundaryBefore, headerRows, MIN_WIDTH, type MergedColumn } from './registerColumns'

export interface RegisterTableProps<Row = Order> {
  /** The `columnWidths` key: `register`, `pricing`, a module view's id (D14). */
  viewId: string
  columns: MergedColumn<Row>[]
  rows: Row[]
  /** Absent: the rows are plain, with no tab stop, click, pointer or row name
   *  (SA-854: a list nobody opens a row of, Route studies' fuel prices). */
  onSelect?: (row: Row) => void
  ariaLabel: string
  /** Default `row.id`. */
  rowKey?: (row: Row) => string
  /** Default `orderNumber`: a caller whose rows are not orders passes its own. */
  rowLabel?: (row: Row) => string
  /** The row you came back from (D15): `aria-current` and a primary wash. */
  markedRowId?: string | null
  /** The line across every column when `rows` is empty. */
  empty?: string
  /** The card's last row, outside the scroll area (the pager). */
  footer?: ReactNode
  busy?: boolean
  /** The list's sort (SA-1124). A key no column names marks no header. */
  sort?: RegisterSort | null
  /** Absent: no header is a sort button. Present: a column with a `sortKey`
   *  carries one, and a click hands the cycle's answer here (SA-1185:
   *  `clientSort.cycleSort` from the column's `sortFirst`, else the register's). */
  onSort?: (next: RegisterSort | null) => void
}

// SA-468 ladder: the header is 11px semibold uppercase at the wireframe's
// .06em — two properties apart from the 12.5px body and from the 11.5px
// muted secondary lines inside cells. On the <tr>, so the SA-462 `th`
// baseline does not read it.
const HEADER_ROW = 'bg-muted text-left text-[11px] font-semibold text-muted-foreground uppercase tracking-[.06em] whitespace-nowrap'
const RULE = 'border-l border-border'
const STEP = 8
const STEP_SHIFT = 16

/** The nearest ancestor that scrolls vertically (the app shell's <main>). */
function scrollParent(el: HTMLElement | null): HTMLElement | null {
  for (let node = el?.parentElement ?? null; node; node = node.parentElement) {
    const overflow = getComputedStyle(node).overflowY
    if (overflow === 'auto' || overflow === 'scroll') return node
  }
  return null
}

type Rove = 'previous' | 'next' | 'first' | 'last'
const ROVE_KEYS: Record<string, Rove> = { ArrowUp: 'previous', ArrowDown: 'next', Home: 'first', End: 'last' }

/** The 7px handle at a header's right edge (spec §8.3 "Resize"): a pointer
 *  drag sets the width live and commits on release — or on a cancelled
 *  pointer, so a drag the browser takes over still lands what it had and
 *  the next drag starts clean; ArrowLeft / ArrowRight move it 8px (16 with
 *  Shift) and commit at once; double-click restores the default. The
 *  handles share ONE Tab stop (SA-464 b, R1): ArrowUp / ArrowDown and
 *  Home / End move focus between them, because Left / Right are the resize. */
function ResizeHandle({
  column, width, tabStop, onLive, onCommit, onReset, onRove, onFocus,
}: {
  column: Pick<MergedColumn, 'id' | 'header' | 'minWidth'>
  width: number
  /** The one handle Tab reaches: a roving tabindex. */
  tabStop: boolean
  onLive: (width: number | null) => void
  onCommit: (width: number) => void
  onReset: () => void
  onRove: (to: Rove) => void
  onFocus: () => void
}) {
  const min = column.minWidth ?? MIN_WIDTH
  const drag = useRef<{ startX: number; startWidth: number; width: number } | null>(null)

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    e.preventDefault(); e.stopPropagation()
    // happy-dom has no pointer capture; a browser keeps the move events on
    // the handle once the pointer leaves it.
    e.currentTarget.setPointerCapture?.(e.pointerId)
    drag.current = { startX: e.clientX, startWidth: width, width }
    e.currentTarget.dataset.dragging = ''
  }
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return
    // Whole pixels: a stored 236.5 is a preference nobody chose.
    const next = Math.max(min, Math.round(drag.current.startWidth + (e.clientX - drag.current.startX)))
    drag.current.width = next
    onLive(next)
  }
  const end = (e: PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return
    const { startWidth, width: final } = drag.current
    drag.current = null
    // SA-468: the solid line is a DOM mark, not state: a drag re-renders
    // through `onLive` anyway, and the mark must clear however it ends.
    delete e.currentTarget.dataset.dragging
    onLive(null)
    // A click with no movement — and the two down/up pairs of every
    // double-click — commits nothing (SA-464 a): storing the default as a
    // number would pin it, and a later change to the spec's default would
    // never reach that person.
    if (final !== startWidth) onCommit(final)
    // After the commit, never before it: releasing an inactive pointer (a
    // cancelled touch) throws NotFoundError, and a throw here must not
    // strand the live width.
    try { e.currentTarget.releasePointerCapture?.(e.pointerId) } catch { /* already released */ }
  }
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const rove = ROVE_KEYS[e.key]
    if (rove) { e.preventDefault(); onRove(rove); return }
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
    e.preventDefault()
    const delta = (e.shiftKey ? STEP_SHIFT : STEP) * (e.key === 'ArrowLeft' ? -1 : 1)
    onCommit(Math.max(min, width + delta))
  }

  return (
    <Tip text={RESIZE_TIP}>
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${column.header}`}
      aria-valuenow={width}
      aria-valuemin={min}
      data-column={column.id}
      tabIndex={tabStop ? 0 : -1}
      onFocus={onFocus}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={end}
      onPointerCancel={end}
      onDoubleClick={(e) => { e.stopPropagation(); onReset() }}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={onKeyDown}
      // The 7px grab zone stays inside the column; the 1px line it draws
      // sits ON the column boundary (SA-468) — visible at rest, brighter on
      // hover or focus, solid while dragging. An overhang into the next
      // `th` would be clipped (`overflow-hidden`) and paint under it.
      className="group/handle absolute top-0 right-0 h-full w-[7px] cursor-col-resize touch-none select-none focus-visible:outline-none"
    >
      <span aria-hidden="true" data-slot="resize-line"
        className="pointer-events-none absolute inset-y-0 right-0 w-px bg-border transition-colors group-hover/handle:bg-primary/50 group-focus-visible/handle:bg-primary/50 group-data-[dragging]/handle:bg-primary" />
    </div>
    </Tip>
  )
}

export function RegisterTable<Row>(props: RegisterTableProps<Row>): ReactElement
// The Order signature LAST: `Parameters<typeof RegisterTable>` reads the last
// overload, so a caller typed on it (the table's own tests) still sees Order.
export function RegisterTable(props: RegisterTableProps<Order>): ReactElement
export function RegisterTable<Row>({
  viewId, columns, rows, onSelect, ariaLabel,
  rowKey = (r) => (r as unknown as { id: string }).id,
  rowLabel = (r) => (r as unknown as Order).orderNumber,
  markedRowId = null, empty, footer, busy, sort = null, onSort,
}: RegisterTableProps<Row>): ReactElement {
  const stored = useSettingsStore((s) => s.ui.records.columnWidths[viewId])
  // A width mid-drag, before it is written to the store on pointer-up.
  const [live, setLive] = useState<Record<string, number>>({})
  const widths = useMemo(
    () => Object.fromEntries(columns.map((c) => [c.id, live[c.id] ?? columnWidth(c, stored?.[c.id])])),
    [columns, live, stored],
  )
  const total = columns.reduce((sum, c) => sum + widths[c.id], 0)
  const header = useMemo(() => headerRows(columns), [columns])

  const setRecordsPrefs = useSettingsStore((s) => s.setRecordsPrefs)
  // The whole map, spread each time: `setRecordsPrefs` merges one level only.
  const write = useCallback((id: string, width: number | null) => {
    const all = useSettingsStore.getState().ui.records.columnWidths
    const view = { ...(all[viewId] ?? {}) }
    if (width === null) delete view[id]
    else view[id] = width
    setRecordsPrefs({ columnWidths: { ...all, [viewId]: view } })
  }, [viewId, setRecordsPrefs])
  const setLiveWidth = useCallback((id: string, width: number | null) => {
    setLive((prev) => {
      const next = { ...prev }
      if (width === null) delete next[id]
      else next[id] = width
      return next
    })
  }, [])

  // The card's top offset (the View-as banner and, below lg, the phone's top
  // bar; then the sticky page header and the gap above the card — whatever
  // the scroll position) and the first header
  // row's height, so the card fits the viewport and a second header row
  // pins under the first (spec §8.3). Measured, never guessed, and re-read
  // whenever the sticky header resizes: it grows when the Filters panel
  // opens. `--register-chrome` is the allowance BELOW the card (the list
  // block's bottom gap plus main's bottom padding, 2.5rem at lg); a page in
  // another shell may set it.
  const cardRef = useRef<HTMLDivElement>(null)
  const firstRowRef = useRef<HTMLTableRowElement>(null)
  const [measure, setMeasure] = useState<{ top: number; row1: number }>({ top: 0, row1: 0 })
  const headerRowCount = header.length
  useLayoutEffect(() => {
    if (typeof ResizeObserver === 'undefined') return
    const head = document.querySelector<HTMLElement>('[data-slot="sticky-page-header"]')
    const card = cardRef.current
    const row1 = firstRowRef.current
    const observer = new ResizeObserver(() => {
      const scroller = scrollParent(card)
      setMeasure({
        top: card ? card.getBoundingClientRect().top + (scroller?.scrollTop ?? 0) : 0,
        row1: row1?.getBoundingClientRect().height ?? 0,
      })
    })
    if (head) observer.observe(head)
    if (row1) observer.observe(row1)
    // The card's parent too: an error line appearing above the card (a
    // failed bulk read, the page's own banner) moves the card without
    // resizing the page header.
    if (card?.parentElement) observer.observe(card.parentElement)
    return () => observer.disconnect()
  }, [headerRowCount])
  const cardStyle = {
    '--register-top': `${measure.top}px`,
    '--register-row1': `${measure.row1}px`,
    // Floored at 16rem: at phone width with the Filters panel open the
    // sticky header can eat the viewport; the card keeps a few rows and the
    // page scrolls instead of showing an empty card.
    maxHeight: 'max(calc(100dvh - var(--register-top, 0px) - var(--register-chrome, 2.5rem)), 16rem)',
  } as CSSProperties

  // One Tab stop for every handle (SA-464 b, R1): the last handle focused
  // holds it, the first column's until then. Focus moves by column order —
  // the same order across both header rows of a grouped table.
  const [activeHandle, setActiveHandle] = useState<string | null>(null)
  const tabStopId = activeHandle && columns.some((c) => c.id === activeHandle) ? activeHandle : columns[0]?.id ?? null
  const rove = useCallback((from: string, to: Rove) => {
    const i = columns.findIndex((c) => c.id === from)
    const last = columns.length - 1
    const j = to === 'first' ? 0 : to === 'last' ? last : to === 'previous' ? Math.max(0, i - 1) : Math.min(last, i + 1)
    const target = columns[j]
    if (!target) return
    setActiveHandle(target.id)
    cardRef.current?.querySelector<HTMLElement>(`[role="separator"][data-column="${target.id}"]`)?.focus()
  }, [columns])

  // The sorted direction of a column, or null: only a sortable header of
  // the sorted key reads as sorted (a sort no column names marks none).
  const sortedOf = (column: MergedColumn<Row>): SortDir | null =>
    onSort && column.sortKey && sort?.key === column.sortKey ? sort.dir : null
  const selectable = onSelect !== undefined
  const select = useCallback((row: Row) => onSelect?.(row), [onSelect])
  const onRowKey = (row: Row) => (e: KeyboardEvent<HTMLTableRowElement>) => {
    if (e.target !== e.currentTarget) return
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(row) }
  }

  return (
    <div ref={cardRef} data-slot="register-card" style={cardStyle} className="flex flex-col rounded-lg border border-border bg-card print:max-h-none!">
      <div data-slot="register-scroll" className="min-h-0 flex-1 overflow-auto print:overflow-visible">
        <table aria-label={ariaLabel} aria-busy={busy || undefined} className="text-[12.5px]"
          style={{ tableLayout: 'fixed', width: total }}>
          <colgroup>
            {columns.map((c) => <col key={c.id} style={{ width: widths[c.id] }} />)}
          </colgroup>
          <thead>
            {header.map((cells, rowIndex) => (
              <tr key={rowIndex} ref={rowIndex === 0 ? firstRowRef : undefined} className={HEADER_ROW}>
                {cells.map((cell) => cell.column ? (
                  <th key={cell.key} scope="col" rowSpan={cell.rowSpan}
                    // The header's own name: the resize handle and the sort
                    // button inside it are separate controls and must not
                    // join the column's name.
                    aria-label={cell.ariaLabel ?? cell.column.header}
                    aria-sort={ariaSort(sortedOf(cell.column))}
                    // A sticky cell is positioned, so the absolute handle
                    // sits at ITS right edge; `relative` would only be merged
                    // away by tailwind-merge as a conflicting position. A
                    // header longer than its column ends in an ellipsis, never
                    // mid-word.
                    className={cn('sticky z-10 bg-muted overflow-hidden text-ellipsis', rowIndex === 0 ? 'top-0' : 'top-[var(--register-row1,0px)]', cell.column.headerClassName, cell.boundary && RULE)}>
                    <SortHeaderLabel header={cell.column.header} title={cell.column.title} className="uppercase"
                      sorted={sortedOf(cell.column)}
                      onSort={onSort && cell.column.sortKey
                        ? () => onSort(cycleSort(sort, cell.column!.sortKey!, cell.column!.sortFirst ?? firstClick(cell.column!.sortKey!)))
                        : undefined} />
                    <ResizeHandle
                      column={cell.column}
                      width={widths[cell.column.id]}
                      tabStop={cell.column.id === tabStopId}
                      onLive={(w) => setLiveWidth(cell.column!.id, w)}
                      onCommit={(w) => write(cell.column!.id, w)}
                      onReset={() => write(cell.column!.id, null)}
                      onRove={(to) => rove(cell.column!.id, to)}
                      onFocus={() => setActiveHandle(cell.column!.id)}
                    />
                  </th>
                ) : (
                  <th key={cell.key} scope="colgroup" colSpan={cell.colSpan}
                    className={cn('sticky top-0 z-10 bg-muted px-2.5 py-1.5 text-center border-b border-border', cell.boundary && RULE)}>
                    {cell.group}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-border">
            {rows.length === 0 && empty && (
              <tr><td colSpan={columns.length} className="px-2.5 py-6 text-center text-muted-foreground">{empty}</td></tr>
            )}
            {rows.map((row) => {
              const id = rowKey(row)
              const marked = markedRowId !== null && id === markedRowId
              return (
                <tr key={id} aria-label={selectable ? rowLabel(row) : undefined} aria-current={marked ? 'true' : undefined}
                  tabIndex={selectable ? 0 : undefined}
                  onClick={selectable ? () => select(row) : undefined} onKeyDown={selectable ? onRowKey(row) : undefined}
                  className={cn(selectable && 'cursor-pointer transition-colors hover:bg-muted focus-visible:outline-2 focus-visible:outline-primary', marked && 'bg-primary/5')}>
                  {columns.map((column, i) => {
                    const value = column.render(row)
                    const content = column.clamp && typeof value === 'string' ? <ClampedText text={value} /> : value
                    return (
                      // A nowrap cell wider than its column clips at the
                      // edge (the handle widens it) rather than spilling into
                      // its neighbour, which a fixed layout would let it do.
                      <td key={column.id} className={cn('overflow-hidden', column.cellClassName, boundaryBefore(columns, i) && RULE)}>
                        {content}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {footer !== undefined && (
        <div data-slot="register-footer" className="border-t border-border px-3 empty:hidden">{footer}</div>
      )}
    </div>
  )
}
